import type { CardAccount } from '../api/client'

// Card payments are shown only when the server has them configured
// (available, Ruling 14). Until then the Menu entry, the panel, Today's banner
// and every card action (Card · pay link, Show pay link, Refund) stay
// hidden. An account not loaded yet, or that failed to load, counts as
// unavailable.

// cardPaymentsShown is whether anything card-related appears at all: the Menu
// entry, the panel, and Refund on money already taken by card.
export function cardPaymentsShown(account: CardAccount | null | undefined): boolean {
  return account?.available === true
}

// connectStartable is whether the embedded components may start: once Stripe
// has approved the account, switched on or not. Setup happens on Stripe's own
// page, so nothing embedded is needed before then; and the server refuses an
// account session before an account exists.
export function connectStartable(account: CardAccount | null | undefined): boolean {
  return cardPaymentsShown(account) && account!.status === 'active'
}

// canTakeCardPayments is whether she can make a pay link now.
export function canTakeCardPayments(account: CardAccount | null | undefined): boolean {
  return cardPaymentsShown(account) && account!.ready
}

// cardNeedsAttention is whether Today shows Stripe's notification banner:
// Stripe wants more information, or has restricted the capability.
export function cardNeedsAttention(account: CardAccount | null | undefined): boolean {
  return cardPaymentsShown(account) && (account!.needs_attention || account!.status === 'restricted')
}

// accountSettling is whether the panel should keep re-checking the account
// on its own: her details are being checked, she has been approved but card
// payments are not switched on yet, or she has just come back from Stripe's
// form (justBack) and its word has not arrived yet. Nothing she does in the
// app moves it on.
export function accountSettling(account: CardAccount | null | undefined, justBack: boolean): boolean {
  if (!cardPaymentsShown(account) || account!.ready) return false
  const a = account!
  if (a.status === 'in_review') return true
  if (a.status === 'active') return !a.enabled
  return (a.status === 'onboarding' || a.status === 'restricted') && justBack
}
